// Copyright © 2021 Move Closer

import { Component, Prop, Vue } from 'vue-property-decorator'

interface TileSliderIndicatorConfig {
  slidesCount: number;
  activeIndex: number;
  durationMs?: number;
  isPaused?: boolean;
  isTransitioning?: boolean;
}

/**
 * Tile slider indicator component
 * Displays progress indicators for slider functionality
 */
@Component<TileSliderIndicator>({
  name: 'TileSliderIndicator',
  template: `
    <div v-if="shouldShow" class="tile__slider-indicator">
      <button v-for="segment in segments" :key="segment.key" class="tile__slider-indicator__segment"
          :class="segment.cssClasses" @click="() => goTo(segment.key)">
        <span class="tile__slider-indicator__segment__bar" :style="segment.barStyle" />
      </button>
    </div>
  `
})
export class TileSliderIndicator extends Vue {
  @Prop({
    type: Object,
    required: false,
    default: null
  })
  public readonly sliderIndicator!: TileSliderIndicatorConfig | null

  /**
   * Determines whether the slider indicator should be shown
   */
  public get shouldShow (): boolean {
    return !!(this.sliderIndicator && this.sliderIndicator.slidesCount > 1)
  }

  /**
   * Prepared data for slider indicator segments
   */
  public get segments (): Array<{
    key: number;
    cssClasses: { [key: string]: boolean };
    barStyle: { [key: string]: string };
  }> {
    if (!this.sliderIndicator) {
      return []
    }

    const segments = []
    for (let i = 0; i < this.sliderIndicator.slidesCount; i++) {
      const isActive = i === this.sliderIndicator.activeIndex
      const segment = {
        key: i,
        cssClasses: {
          '--active': isActive,
          '--paused': isActive && !!this.sliderIndicator?.isPaused
        },
        barStyle: this.getSliderBarStyle(isActive)
      }
      segments.push(segment)
    }

    return segments
  }

  public goTo (segmentKey: number): void {
    this.$emit('go-to', segmentKey)
  }

  /**
   * Gets the style for the slider progress bar
   */
  private getSliderBarStyle (isActive: boolean): { [key: string]: string } {
    if (!isActive || !this.sliderIndicator || !this.sliderIndicator.durationMs) {
      return {}
    }

    return {
      animationDuration: `${this.sliderIndicator.durationMs}ms`
    }
  }
}
